/**
 * Inspections List Page.
 *
 * Lists inspections for the current organisation in a filterable DataTable,
 * mirroring the Claims list surface: dynamic status pills, the shared
 * compact toolbar (saved views + search + columns + record-metadata filter
 * fly-out), and the standard list-page header with a compact action button.
 *
 * A Priority | Pool landing split sits above the toolbar: Priority = the
 * unplanned world (claim-triggered / manual / API, most urgent first); Pool =
 * the bulk registration programme (pool-staged registrations). The split is
 * a server-side `bucket` scope on the list endpoint; the pills, facets, and
 * saved views all compute over whichever bucket is loaded.
 *
 * "Priority" and "Pool" are OUR words, not the client's - OC never supplied
 * their own terms for this split. The three tab labels are therefore read
 * through `t()` with a seeded default, so renaming them when the client does
 * have an opinion is a seed change rather than a code change.
 *
 * The list endpoint returns a flat array (up to 200 rows), so status, type,
 * result, and free-text search are all applied client-side over the loaded
 * set. Status pills are built from the loaded rows' lifecycle status counts;
 * type + result are exposed as facets in the filter fly-out.
 *
 * Gated by the `inspections` feature flag - tenants without the flag
 * do not render this route. The flag check at the AppShell route level
 * keeps them from even hitting this code path.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  BarChart3,
  CalendarDays,
  ClipboardCheck,
  Flag,
  ListChecks,
  Route,
  Wrench,
} from "lucide-react";
import { AppShell } from "@/admin/components/app-shell";
import { Button } from "@/shared/components/ui/button";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { Badge } from "@/shared/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { ListPageToolbar } from "@/admin/components/list-page-toolbar";
import { EmptyState } from "@/admin/components/shared/empty-state";
import { NotificationDot } from "@/shared/components/notifications/notification-dot";
import { useTableColumnPreferences } from "@/admin/hooks/use-table-column-preferences";
import { useTableFilterPreferences } from "@/admin/hooks/use-table-filter-preferences";
import { useTableState } from "@/admin/hooks/use-table-state";
import { localDate } from "@/admin/lib/time-range-params";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useSavedListView } from "@/admin/hooks/use-saved-list-view";
import type { StatusPillItem } from "@/admin/components/shared/status-pills";
import type { ProductFilterFacet } from "@/shared/components/ui/product-filter-menu";
import { cn } from "@/shared/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  useInspectionsPage,
  useInspectionChoices,
  useInspectionSubTypes,
  useBulkMarkInspectionPriority,
  type InspectionRow,
} from "@/admin/hooks/use-inspections";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { FeatureGate } from "@/admin/components/feature-gate";
import { FLAGS } from "@/admin/hooks/use-feature-flag";
import { useFormatters } from "@/shared/lib/formatting";

const TABLE_ID = "inspections";

function formatPropertyAddress(row: InspectionRow): string {
  const parts = [
    row.property_address_line_1,
    row.property_city,
    row.property_state,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
}

function buildLabelMap(
  options: { value: string; label: string }[] | undefined,
): Record<string, string> {
  if (!options) return {};
  return Object.fromEntries(options.map((o) => [o.value, o.label]));
}

/** Today shifted by whole days, as a local `YYYY-MM-DD`. */
function dayFrom(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return localDate(d);
}

/** Created-date presets (past-oriented) shown in the filter fly-out. */
const CREATED_PRESETS: { value: string; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const CREATED_DAYS: Record<string, number> = { today: 0, "7d": 7, "30d": 30, "90d": 90 };

/** A created preset as the server's `created_after` (local date, inclusive). */
function createdWindow(preset: string | undefined): Record<string, string> {
  return preset && preset in CREATED_DAYS
    ? { created_after: dayFrom(-CREATED_DAYS[preset]) }
    : {};
}

/** Scheduled-date presets (future-oriented, plus overdue/unscheduled). */
const SCHEDULED_PRESETS: { value: string; label: string }[] = [
  { value: "unscheduled", label: "Unscheduled" },
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Next 7 days" },
  { value: "30d", label: "Next 30 days" },
];

/** A scheduled preset as the server's scheduled params (local dates, inclusive). */
function scheduledWindow(preset: string | undefined): Record<string, string> {
  switch (preset) {
    case "unscheduled":
      return { scheduled: "none" };
    case "overdue":
      return { scheduled_to: dayFrom(-1) };
    case "today":
      return { scheduled_from: dayFrom(0), scheduled_to: dayFrom(0) };
    case "7d":
      return { scheduled_from: dayFrom(0), scheduled_to: dayFrom(7) };
    case "30d":
      return { scheduled_from: dayFrom(0), scheduled_to: dayFrom(30) };
    default:
      return {};
  }
}

export function InspectionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { can } = usePermissions();
  const canCreate = can("inspections.create");
  // Narrow codename - a Resolution Specialist holds it without holding the
  // coarse inspections.edit permission, so do not gate marking on edit.
  const canMarkPriority = can("inspections.mark_priority");
  // One hook instance for the whole table: the bulk endpoint takes a list, so
  // a single-row toggle is just a list of one. Avoids a hook per row.
  const markPriority = useBulkMarkInspectionPriority();

  const [search, setSearch] = useState("");
  // Landing split: Priority = unplanned work (claim/manual/API triggered, most
  // urgent first); Pool = the bulk registration programme.
  const [bucket, setBucket] = useState<"all" | "priority" | "pool">("all");
  // Status pill selection (a lifecycle_status value) and the record-metadata
  // filter fly-out selection, persisted per tenant+table.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [
    filterValues,
    setFilterValues,
    { applyFilterValues, restoreSavedFilterValues },
  ] = useTableFilterPreferences(TABLE_ID);

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnVisibility,
    saveColumnOrder,
    applyDefaultColumnPreferences,
  } = useTableColumnPreferences(TABLE_ID);

  // The platform's standard page: every filter, the status pills and sorting
  // run on the server, so every inspection is reachable however many there are.
  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID);
  const debouncedSearch = useDebouncedValue(search);
  const params = useMemo(() => {
    const p: Record<string, string> = {
      ...queryParams,
      ...createdWindow(filterValues.created?.[0]),
      ...scheduledWindow(filterValues.scheduled?.[0]),
    };
    if (bucket !== "all") p.bucket = bucket;
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    // The status pill and the fly-out status facet combine (AND): a pill the
    // facet leaves out matches nothing.
    const statuses = filterValues.status ?? [];
    if (statusFilter) {
      p.status =
        statuses.length && !statuses.includes(statusFilter) ? "none" : statusFilter;
    } else if (statuses.length) {
      p.status = statuses.join(",");
    }
    for (const key of ["inspector", "state", "sub_type", "result"]) {
      const values = filterValues[key] ?? [];
      if (values.length) p[key] = values.join(",");
    }
    return p;
  }, [queryParams, filterValues, bucket, debouncedSearch, statusFilter]);
  const { data, isLoading, isFetching, isError, refetch } = useInspectionsPage(params);
  const rows = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  const { data: choices } = useInspectionChoices();
  const { data: subTypes } = useInspectionSubTypes();
  const resultLabels = useMemo(
    () => buildLabelMap(choices?.results),
    [choices],
  );

  // Pills in the tenant's status order, counted on the server before the
  // status filter, so each reflects the other active filters.
  const statusCounts = data?.status_counts ?? {};
  const statusPills: StatusPillItem[] = (choices?.lifecycle_statuses ?? [])
    .filter((o) => statusCounts[o.value])
    .map((o) => ({ key: o.value, label: o.label, count: statusCounts[o.value] }));
  const statusTotal = Object.values(statusCounts).reduce((n, c) => n + c, 0);

  // Record-metadata facets in the filter fly-out. Inspectors and states come
  // from the server (every inspection, not one page); the rest from config.
  const serverFacets = data?.filter_options;
  const filterFacets: ProductFilterFacet[] = useMemo(() => {
    const facets: ProductFilterFacet[] = [];
    if (choices?.lifecycle_statuses?.length) {
      facets.push({
        key: "status",
        label: "Status",
        kind: "field",
        options: choices.lifecycle_statuses,
      });
    }
    facets.push(...(serverFacets ?? []));
    facets.push({
      key: "scheduled",
      label: "Scheduled",
      kind: "field",
      type: "date",
      options: SCHEDULED_PRESETS,
    });
    facets.push({
      key: "created",
      label: "Created",
      kind: "field",
      type: "date",
      options: CREATED_PRESETS,
    });
    if (subTypes?.length) {
      facets.push({
        key: "sub_type",
        label: "Type",
        kind: "field",
        options: subTypes.map((st) => ({ value: st.key, label: st.label })),
      });
    }
    if (choices?.results?.length) {
      facets.push({
        key: "result",
        label: "Result",
        kind: "field",
        options: choices.results,
      });
    }
    return facets;
  }, [subTypes, choices, serverFacets]);

  const hasFilters = Boolean(
    statusFilter ||
      debouncedSearch.trim() ||
      bucket !== "all" ||
      Object.values(filterValues).some((values) => values.length),
  );
  const showEmptyState =
    !isLoading && !isFetching && totalCount === 0 && !hasFilters;

  const columns: DataTableColumn<InspectionRow>[] = useMemo(
    () => [
      {
        id: "code",
        header: "Code",
        accessorKey: "code",
        cell: (row) => (
          <span className="inline-flex items-center gap-2">
            {row.spotlight?.active ? (
              <NotificationDot
                level={row.spotlight.level}
                title={row.spotlight.title || "Needs attention"}
              />
            ) : null}
            <span
              className={cn(
                "font-mono text-[12px]",
                // New-inspection emphasis: darken + bolden the code for a
                // freshly-created, not-yet-scheduled inspection (spotlight.is_new).
                // Clears when the "awaiting scheduling" notification is read or the
                // inspection leaves its initial state. Distinct from the attention
                // dot (any unread), so unread results/assignments don't bold.
                row.spotlight?.is_new
                  ? "font-semibold text-[var(--ink)]"
                  : "text-[var(--ink-3)]",
              )}
            >
              {row.code}
            </span>
            {row.is_priority && (
              <Badge
                variant="destructive"
                className="px-1.5 py-0 text-[10px] uppercase tracking-[0.08em]"
                title={row.priority_reason || "Marked priority"}
              >
                Priority
              </Badge>
            )}
          </span>
        ),
      },
      {
        // Right after the code, so it is in view without scrolling, the same
        // on every list page.
        id: "updated_at",
        header: t("common.lastActivity", "Last Activity"),
        sortable: true,
        sortField: "updated_at",
        sortKind: "date",
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">
            {row.updated_at ? formatDate(row.updated_at) : "-"}
          </span>
        ),
      },
      {
        // Deliberately its own column rather than folded into the code cell:
        // Source (where the work came from) and Priority (the team saying it
        // is urgent) are independent axes, so the flag has to be settable
        // without touching anything else on the row.
        id: "priority",
        header: "Priority",
        accessorKey: "is_priority",
        cell: (row) =>
          canMarkPriority ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              title={row.is_priority ? "Remove priority" : "Mark priority"}
              onClick={(e) => {
                e.stopPropagation();
                markPriority.mutate({
                  inspection_uuids: [row.uuid],
                  is_priority: !row.is_priority,
                });
              }}
            >
              <Flag
                size={13}
                className={
                  row.is_priority
                    ? "fill-[var(--status-error)] text-[var(--status-error)]"
                    : "text-[var(--ink-3)]"
                }
              />
            </Button>
          ) : row.is_priority ? (
            <Flag
              size={13}
              className="fill-[var(--status-error)] text-[var(--status-error)]"
            />
          ) : null,
      },
      {
        id: "sub_type",
        header: "Type",
        accessorKey: "sub_type_label",
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span>{row.sub_type_label}</span>
            {/* What KIND of claim caused this, and what is wrong. "Quality
                Claim Inspection" alone does not distinguish shingles curling
                on a house from a dealer's stock. */}
            {row.claim_context?.claim_type_label && (
              <span className="text-[10px] text-[var(--ink-3)]">
                {row.claim_context.claim_type_label}
                {row.claim_context.defect
                  ? ` \u00b7 ${row.claim_context.defect}`
                  : ""}
              </span>
            )}
          </div>
        ),
      },
      // An inspector picking work off this queue needs to know what the job
      // is attached to. Without it the queue is a wall of INSP codes and the
      // only way to find out is to open each one.
      {
        id: "claim",
        header: "Claim",
        accessorKey: "claim_ref",
        cell: (row) =>
          row.claim_ref && row.claim_uuid ? (
            <div className="flex flex-col items-start gap-0.5">
              <Button
                variant="link"
                className="h-auto p-0 font-mono text-[12px]"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/claims/${row.claim_uuid}`);
                }}
              >
                {row.claim_ref}
              </Button>
              {/* The claim's own service level, so an inspector reads the
                  deadline they are working to straight off the queue. Job
                  Stop runs a 7 day SLA; a workflow that declares none shows
                  nothing rather than an invented default. */}
              {row.claim_service_level?.is_priority &&
              row.claim_service_level?.sla_days != null ? (
                <span className="inline-flex items-center gap-1 rounded-lg border-[0.5px] border-red-700 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-red-700">
                  {t("inspections.list.daySla", {
                    defaultValue: "{{count}} Day SLA",
                    count: row.claim_service_level.sla_days,
                  })}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "lifecycle_status",
        cell: (row) => <StatusBadge status={row.lifecycle_status} />,
      },
      {
        id: "result",
        header: "Result",
        accessorKey: "result",
        cell: (row) =>
          row.result ? (resultLabels[row.result] ?? row.result) : "-",
      },
      {
        id: "scheduled_for",
        header: "Scheduled",
        accessorKey: "scheduled_for",
        cell: (row) =>
          row.scheduled_for ? formatDate(row.scheduled_for) : "-",
      },
      {
        id: "inspector",
        header: "Inspector",
        accessorKey: "inspector_name",
        cell: (row) => row.inspector_name || "-",
      },
      {
        id: "contractor",
        header: "Contractor",
        accessorKey: "contractor_name",
        cell: (row) => row.contractor_name || "-",
      },
      {
        id: "property",
        header: "Property",
        cell: (row) => formatPropertyAddress(row),
      },
      {
        id: "state",
        header: "State",
        accessorKey: "property_state",
        cell: (row) => row.property_state || "-",
      },
      {
        id: "evidence",
        header: "Evidence",
        accessorKey: "evidence_count",
        cell: (row) =>
          row.evidence_count > 0 ? `${row.evidence_count} files` : "-",
      },
    ],
    [resultLabels, formatDate, canMarkPriority, markPriority, navigate, t],
  );

  // Saved views hold columns, order, sort, the Priority / Pool tab, the status
  // pill and the Filters menu (same infra as Claims).
  const savedView = useSavedListView({
    tableId: TABLE_ID,
    columns,
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility,
    applyDefaultColumnPreferences,
    columnOrder,
    setColumnOrder,
    saveColumnOrder,
    slices: {
      sorting: { value: sorting ?? null, apply: setSorting, empty: null },
      scope: {
        value: bucket,
        apply: (v) => setBucket(v as "all" | "priority" | "pool"),
        empty: "all",
      },
      status: { value: statusFilter, apply: setStatusFilter, empty: null },
      filterValues: {
        value: filterValues,
        apply: applyFilterValues,
        savePreference: setFilterValues,
        restorePreference: restoreSavedFilterValues,
        empty: {},
      },
    },
  });

  return (
    <AppShell>
      <main className="page-container py-10 flex flex-col gap-6">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="clara-title">Inspections</h1>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              {`${totalCount} inspection${totalCount === 1 ? "" : "s"}${
                hasFilters ? " match" : " for this organisation"
              }`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto sm:mb-1">
            {/* Smart Schedule entry points. Their routes gate on `scheduling`,
                so hide the buttons with the same flag - otherwise they navigate
                straight into a redirect. */}
            <FeatureGate flag={FLAGS.SCHEDULING}>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/queue")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <ListChecks size={13} className="mr-1.5" />
                Pool
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/queue?tab=itineraries")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <Route size={13} className="mr-1.5" />
                Itineraries
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/inspections/scheduling")}
                className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
              >
                <CalendarDays size={13} className="mr-1.5" />
                Scheduling
              </Button>
            </FeatureGate>
            <Button
              variant="outline"
              onClick={() => navigate("/inspections/punch-list")}
              className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
            >
              <Wrench size={13} className="mr-1.5" />
              {t("inspections.landing.punchList", "Punch list")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/inspections/dashboard")}
              className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
            >
              <BarChart3 size={13} className="mr-1.5" />
              Dashboard
            </Button>
            {canCreate && (
              <Button
                onClick={() => navigate("/inspections/new")}
                className="rounded-[4px] px-4 text-[11px] font-normal uppercase tracking-[0.1em] bg-[var(--green)] hover:bg-[var(--green-hover)] text-white border-none"
              >
                + New Inspection
              </Button>
            )}
          </div>
        </header>

        {showEmptyState ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No inspections yet"
            description={
              "Inspections will appear here once you create one manually or a rule auto-creates one from a claim or registration."
            }
            action={
              canCreate
                ? {
                    label: "New Inspection",
                    onClick: () => navigate("/inspections/new"),
                  }
                : undefined
            }
          />
        ) : (
          <>
            {/* Priority | Pool split - two worlds of work */}
            <Tabs
              value={bucket}
              onValueChange={(v: string) => {
                savedView.change("scope", v);
                setPage(1);
              }}
            >
              <TabsList variant="pill" className="w-fit">
                <TabsTrigger value="all">
                  {t("inspections.landing.tabAll", "All")}
                </TabsTrigger>
                <TabsTrigger value="priority">
                  {t("inspections.landing.tabPriority", "Priority")}
                </TabsTrigger>
                <TabsTrigger value="pool">
                  {t("inspections.landing.tabPool", "Pool")}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <ListPageToolbar<InspectionRow>
              layout="compact"
              tableId={TABLE_ID}
              search={search}
              onSearchChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              searchPlaceholder="Search code, property, contractor…"
              statusPills={statusPills}
              activeStatusKey={statusFilter}
              onStatusSelect={(key) => {
                savedView.change("status", key);
                setPage(1);
              }}
              statusTotalCount={statusTotal}
              {...savedView.toolbarProps}
              columns={columns}
              activeFilters={[]}
              onFiltersChange={() => {}}
              showColumnToggle={false}
              filterFacets={filterFacets}
              filterValues={filterValues}
              onFilterValuesChange={(values) => {
                savedView.change("filterValues", values);
                setPage(1);
              }}
              columnVisibility={columnVisibility}
              alwaysShownColumns={["code", "status"]}
              columnOrder={columnOrder}
            />

            <DataTable<InspectionRow>
              tableId={TABLE_ID}
              columns={columns}
              data={rows}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              onRetry={refetch}
              onRowClick={(row) => navigate(`/inspections/${row.uuid}`)}
              rowKey="uuid"
              emptyMessage="No inspections match your filters."
              columnVisibility={columnVisibility}
              columnOrder={columnOrder}
              sorting={sorting}
              onSortingChange={(next) => {
                savedView.change("sorting", next);
                setPage(1);
              }}
            />
          </>
        )}
      </main>
    </AppShell>
  );
}
